import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, PartyPopper, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Avatar } from "./StaffAvatar";
import { BOARD_ROLES } from "./common";

const KEY = "ax-toppers";
const lakh = (v) => `₹${(v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
const pos = (n) => `${n} PO${n === 1 ? "" : "s"}`;
const BOARDS = [
  { k: "nbd", name: "New business", rows: (d) => d.new.orders, score: (r) => r.count, info: (r, v) => (v ? `${lakh(r.value)} · ${pos(r.count)}` : pos(r.count)) },
  { k: "inside", name: "Inside sales", rows: (d) => d.new.inside, score: (r) => r.calls + r.meetings, info: (r) => `${r.calls} calls · ${r.meetings} meetings` },
  { k: "repeat", name: "Repeat business", rows: (d) => d.repeat.orders, score: (r) => r.count, info: (r, v) => (v ? `${lakh(r.value)} · ${pos(r.count)}` : pos(r.count)) },
];
const top = (d, b) => { const r = b.rows(d)[0]; return r && b.score(r) > 0 ? r : null; };

const cheer = (d, me) => {
  const now = { label: d.label, ...Object.fromEntries(BOARDS.map((b) => [b.k, top(d, b)?.staff_name || null])) };
  let prev = null;
  try { prev = JSON.parse(localStorage.getItem(KEY) || "null"); } catch { prev = null; }
  localStorage.setItem(KEY, JSON.stringify(now));
  if (!prev || prev.label !== now.label) return [];
  const changed = BOARDS.filter((b) => now[b.k] && now[b.k] !== prev[b.k]);
  changed.forEach((b) => toast.success(now[b.k] === me ? `You just took #1 in ${b.name}!` : `New leader in ${b.name}: ${now[b.k]}`,
    { description: now[b.k] === me ? "Take a bow. Now hold on to that spot." : "Give them a cheer, then go win your spot back.", icon: <PartyPopper className="h-4 w-4 text-primary" />, duration: 10000 }));
  return changed.map((b) => b.k);
};

const Slot = ({ b, r, me, showValue, fresh }) => (
  <div className={`flex items-center gap-3 p-3 border bg-card/60 min-w-0 ${fresh ? "ax-cheer" : ""} ${r?.staff_name === me ? "border-primary" : ""}`} data-testid={`home-topper-${b.k}`}>
    {r ? <div className="relative shrink-0"><Avatar name={r.staff_name} size={44} /><Trophy className="h-4 w-4 absolute -bottom-1 -right-1 drop-shadow" style={{ color: "#d4a017" }} /></div>
      : <div className="h-11 w-11 rounded-full border border-dashed shrink-0" />}
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{b.name}</div>
      <div className="font-semibold truncate text-sm">{r ? (r.staff_name === me ? "You!" : r.staff_name) : "Up for grabs"}</div>
      <div className="text-xs text-muted-foreground truncate num">{r ? b.info(r, showValue) : "First to score takes it"}</div>
    </div>
  </div>
);

export const Toppers = () => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [fresh, setFresh] = useState([]);
  const ran = useRef(false);
  const allowed = BOARD_ROLES.includes(user?.role_key);
  useEffect(() => {
    if (!allowed || ran.current) return;
    ran.current = true;
    api.get("/sales/leaderboard", { params: { period: "fy" } }).then((r) => { setD(r.data); setFresh(cheer(r.data, user?.staff_name)); }).catch(() => {});
  }, [allowed, user?.staff_name]);
  if (!allowed || !d) return null;
  return (
    <div className="ax-panel p-4 mb-6 ax-fade" data-testid="home-toppers">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-semibold"><Trophy className="h-4 w-4" style={{ color: "#d4a017" }} />Toppers {d.label}</div>
        <Link to="/sales/leaderboard" className="text-xs text-primary flex items-center hover:underline" data-testid="home-toppers-link">Full leaderboard<ChevronRight className="h-3 w-3" /></Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {BOARDS.map((b) => <Slot key={b.k} b={b} r={top(d, b)} me={user?.staff_name} showValue={d.show_value} fresh={fresh.includes(b.k)} />)}
      </div>
    </div>
  );
};
